import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import styles from './Table.module.css';

import { useFlexLayout, usePagination, useSortBy, useTable } from 'react-table';
import NumberFormatter from '../Format/NumberFormatter';
import Pagination from './Pagination';

const SortIcon = ({ isSortedDesc }) => {
	if (isSortedDesc === undefined) return <FaSort />;

	if (isSortedDesc) return <FaSortDown />;
	return <FaSortUp />;
};

const Thead = ({
	children,
	column: { isSorted, isSortedDesc, canSort, toggleSortBy, isTextAlignCenter },
	...props
}) => {
	return (
		<div
			className={cx(
				{ [styles.th]: !isTextAlignCenter },
				{ [styles.thc]: isTextAlignCenter },
				{ [styles.sortable]: canSort }
			)}
			onClick={canSort ? () => toggleSortBy(!isSortedDesc) : undefined}
		>
			{children}
			{canSort && (
				<div className={styles.sortIcon}>
					<SortIcon isSorted={isSorted} isSortedDesc={isSortedDesc} />
				</div>
			)}
		</div>
	);
};

const renderHeader = (column) => (props) =>
	(
		<Thead {...props}>
			{typeof column.Header === 'string' ? column.Header : column.Header(props)}
			{/* {typeof column.Header === 'string'
				? console.log('column.Header----', column)
				: console.log('column.Header(props)----', column.Header(props))} */}
		</Thead>
	);

const renderCell = ({ cell, value, numeric }) => {
	if (numeric) {
		return (
			<div {...cell.getCellProps()}>
				<NumberFormatter value={value} />
			</div>
		);
	}
	return <div {...cell.getCellProps()}>{value}</div>;
};

const defaultColumnProps = ({ numeric, ...column }) => ({
	width: '10%',
	Cell: (props) => renderCell({ numeric, ...props }),
	sortable: false,
	accessor: (value) => value[column.id],
	numeric: false,
});

const generateColumns = (columns) => {
	return columns
		.map((column) => ({
			...defaultColumnProps(column),
			...column,
		}))
		.map(({ sortable, hidden, ...column }) => ({
			...column,
			disableSortBy: !sortable,
			Header: renderHeader(column),
		}));
};

const TableHead = ({ headerGroups, modalIsOpen, isNotEvenRowColor }) => {
	return (
		<div className={styles.thead}>
			{headerGroups.map((headerGroup, headerGroupIndex) => (
				<div
					{...headerGroup.getHeaderGroupProps()}
					key={headerGroupIndex}
					className={cx(
						styles.tr,
						isNotEvenRowColor === false && styles.trEvenColumn
					)}
				>
					{headerGroup.headers.map((column, headerColumnIndex) => {
						let cellLength = headerGroup.headers.length;

						let css = modalIsOpen
							? {
									minWidth: cellLength <= 4 ? column.width : '20%',
									width: cellLength <= 4 ? column.width : '20%',
									maxWidth: cellLength <= 4 ? column.width : '20%',
									backgroundColor: '#0176c0',
									borderTopLeftRadius: headerColumnIndex === 0 && '10px',
									borderTopRightRadius:
										headerGroup.headers.length - 1 === headerColumnIndex &&
										'10px',
									display: 'flex',
									justifyContent: 'flex-start',
									alignItems: 'center',
									textAlign: 'center',
									tableLayout: 'fixed',
							  }
							: {
									minWidth: column.width,
									width: column.width,
									maxWidth: column.width,
							  };
						return (
							<div
								{...column.getHeaderProps()}
								key={headerColumnIndex}
								style={css}
							>
								{column.render('Header')}
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
};

const TableBody = ({
	rows,
	prepareRow,
	hasRowField = false,
	modalIsOpen,
	isNotEvenRowColor,
	hasScroll = false,
	...props
}) => {
	if (rows.length === 0) {
		return (
			<>
				{Array(10)
					.fill()
					.map((_, index) => (
						<div key={index} className={cx(styles.tr, styles.emptyRow)}>
							{index === 4 && 'No results found.'}
						</div>
					))}
			</>
		);
	}

	return (
		<div className={cx(styles.tbody)}>
			{rows.map((row, rowIndex) => {
				prepareRow(row);
				return (
					<div
						key={rowIndex}
						{...row.getRowProps()}
						className={cx(
							styles.tr,
							isNotEvenRowColor === false && styles.trEvenColumn,
							{
								[styles.trHeightForFields]: !!hasRowField,
							}
						)}
					>
						{row.cells.map((cell, cellIndex) => {
							let cellLength = row.cells.length;
							let css = modalIsOpen
								? {
										minWidth: cellLength <= 4 ? cell.column.width : '20%',
										width: cellLength <= 4 ? cell.column.width : '20%',
										maxWidth: cellLength <= 4 ? cell.column.width : '20%',
										display: 'flex',
										justifyContent: 'flex-start',
										alignItems: 'center',
										textAlign: 'center',
										tableLayout: 'fixed',
								  }
								: {
										minWidth: cell.column.width,
										width: cell.column.width,
										maxWidth: cell.column.width,
								  };
							return (
								<div
									{...cell.getCellProps()}
									style={css}
									key={cellIndex}
									className={cx(
										styles.td,
										row.values[props.row?.label] == props.row?.value
											? styles[props.row?.css]
											: ''
									)}
								>
									{cell.render('Cell')}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};

export default function Table({
	columns,
	data = [],
	pageSize: initPageSize = 25,
	onPageChange = (_page, _size) => {},
	onSort = (_sortBy, _sort) => {},
	manualPagination = true,
	count = 0,
	sortBy: initSortBy = '',
	sort: initSort = 'asc',
	page: initPageIndex = 1,
	showPagination = true,
	showPageSizeOptions = true,
	tableBodyProps = {},
	showFrom = true,
	className = '',
	preHeader,
	withRowField = false,
	modalIsOpen,
	isNotEvenRowColor = false,
	scrollable = false,
}) {
	const memoColumn = useMemo(() => generateColumns(columns), [columns]);
	const memoData = useMemo(() => data, [data]);
	const pageCount = Math.ceil(count / initPageSize);
	const [initialized, setInitialized] = useState(false);
	const hiddenColumns = columns
		.filter(({ hidden }) => hidden)
		.map(({ id }) => id);

	useEffect(() => {
		setInitialized(true);
	}, []);

	const tableProps = useTable(
		{
			columns: memoColumn,
			data: memoData,
			initialState: {
				pageIndex: initPageIndex - 1,
				sortBy: useMemo(
					() => [
						{
							id: initSortBy,
							desc: initSort.toLowerCase() === 'desc',
						},
					],
					[]
				),
				pageSize: initPageSize,
				hiddenColumns,
			},
			manualSortBy: true,
			manualPagination,
			pageCount,
		},
		useSortBy,
		usePagination,
		useFlexLayout
	);

	const {
		state: { pageIndex, pageSize, sortBy },
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
	} = tableProps;

	const pageProps = {
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		pageSize,
		showFrom,
		goToFirst: () => gotoPage(0),
		goToLast: () => gotoPage(pageCount - 1),
	};

	useEffect(() => {
		if (initialized) {
			onPageChange(pageIndex + 1, pageSize);
		}
	}, [pageIndex, pageSize]);

	useEffect(() => {
		if (initialized) {
			const [{ id, desc } = {}] = sortBy;
			onSort(id, desc ? 'DESC' : 'ASC');
		}
	}, [sortBy]);

	useEffect(() => {
		if (initPageIndex - 1 !== pageIndex) {
			gotoPage(initPageIndex - 1);
		}
	}, [initPageIndex]);

	return (
		<div className={cx('slds-card', styles.paperContainer, className)}>
			<div
				{...getTableProps()}
				className={cx(preHeader && styles.withPreHeader)}
			>
				{preHeader && <div className={styles.preHeader}>{preHeader}</div>}
				<TableHead
					headerGroups={headerGroups}
					modalIsOpen={modalIsOpen}
					isNotEvenRowColor={isNotEvenRowColor}
				/>
				<TableBody
					{...getTableBodyProps(tableBodyProps)}
					rows={page}
					prepareRow={prepareRow}
					hasRowField={withRowField}
					modalIsOpen={modalIsOpen}
					hasScroll={scrollable}
					isNotEvenRowColor={isNotEvenRowColor}
				/>
			</div>
			{showPagination && (
				<div className={styles.pagination}>
					<Pagination
						{...pageProps}
						showPageSizeOptions={showPageSizeOptions}
						pageIndexProps={initPageIndex - 1}
						count={count}
						pageCount={pageCount}
						handlePageCount={(value) => setPageSize(value)}
						canNextPage={true}
						canPreviousPage={true}
					/>
				</div>
			)}
		</div>
	);
}
